import { DEFAULT_FAVICON_URL } from "@/constants/branding";

/**
 * Utility functions for creating watermarked images for sharing
 * Composites profile avatar, site favicon watermark, and artist name
 */

import QRCode from "qrcode";

const SITE_FAVICON_URL = DEFAULT_FAVICON_URL;

/**
 * Loads an image from URL and returns a promise with the HTMLImageElement
 */
function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Creates a watermarked version of the profile avatar with site favicon and artist name
 * @param avatarUrl - URL of the profile avatar image
 * @param artistName - Name of the artist to overlay on the image
 * @param username - Username for profile URL display
 * @param sponsors - Optional array of sponsor data for overlay
 * @returns Promise<Blob> - Watermarked image as blob
 */
export async function createWatermarkedAvatar(
  avatarUrl: string,
  artistName: string,
  username: string,
  sponsors?: Array<{ avatar_url?: string | null; display_name: string }>,
  roleLabel: string = "Artist",
  isVerified: boolean = false,
): Promise<Blob> {
  try {
    // Generate QR code for profile URL
    const profileUrl = `https://paystreem.com/profiles/${username}`;
    const qrCodeDataUrl = await QRCode.toDataURL(profileUrl, {
      width: 720,
      margin: 1,
      color: {
        dark: "#000000",
        light: "#FFFFFF",
      },
    });

    const [avatarImg, faviconImg, qrCodeImg] = await Promise.all([
      loadImageFromUrl(avatarUrl),
      loadImageFromUrl(SITE_FAVICON_URL),
      loadImageFromUrl(qrCodeDataUrl),
    ]);

    // Portrait canvas matching reference
    const W = 1024;
    const H = 1792;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");

    // Palette
    const BEIGE = "#F1ECE6";
    const LAVENDER = "#B9A7E0";
    const LAVENDER_SOFT = "#C9BCE4";
    const DEEP_PURPLE = "#3D1E8C";
    const GRAY = "#3F3A4A";

    // Background
    ctx.fillStyle = BEIGE;
    ctx.fillRect(0, 0, W, H);

    // Decorative circles
    ctx.fillStyle = LAVENDER;
    ctx.beginPath();
    ctx.arc(20, 80, 240, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = LAVENDER_SOFT;
    ctx.beginPath();
    ctx.arc(W - 20, H - 80, 150, 0, 2 * Math.PI);
    ctx.fill();

    // Avatar
    const centerX = W / 2;
    const avatarRadius = 290;
    const avatarCY = 480;

    // Lavender ring
    ctx.fillStyle = LAVENDER;
    ctx.beginPath();
    ctx.arc(centerX, avatarCY, avatarRadius + 18, 0, 2 * Math.PI);
    ctx.fill();
    // White gap
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(centerX, avatarCY, avatarRadius + 8, 0, 2 * Math.PI);
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, avatarCY, avatarRadius, 0, 2 * Math.PI);
    ctx.clip();
    // Cover-fit avatar
    const ar = avatarImg.width / avatarImg.height;
    let dW = avatarRadius * 2;
    let dH = avatarRadius * 2;
    if (ar > 1) dW = dH * ar;
    else dH = dW / ar;
    ctx.drawImage(avatarImg, centerX - dW / 2, avatarCY - dH / 2, dW, dH);
    ctx.restore();

    // Logo badge overlapping bottom of avatar
    const badgeR = 70;
    const badgeY = avatarCY + avatarRadius + 10;
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(centerX, badgeY, badgeR, 0, 2 * Math.PI);
    ctx.fill();
    // subtle ring
    ctx.strokeStyle = "rgba(0,0,0,0.05)";
    ctx.lineWidth = 2;
    ctx.stroke();

    const logoSize = badgeR * 1.25;
    ctx.drawImage(
      faviconImg,
      centerX - logoSize / 2,
      badgeY - logoSize / 2,
      logoSize,
      logoSize,
    );

    // Artist name (uppercase, auto-fit)
    const nameY = badgeY + 220;
    const maxNameWidth = W - 140;
    let nameSize = 170;
    const nameUpper = artistName.toUpperCase();
    ctx.font = `900 ${nameSize}px"Inter","Helvetica Neue", Arial Black, sans-serif`;
    while (ctx.measureText(nameUpper).width > maxNameWidth && nameSize > 60) {
      nameSize -= 4;
      ctx.font = `900 ${nameSize}px"Inter","Helvetica Neue", Arial Black, sans-serif`;
    }
    ctx.fillStyle = DEEP_PURPLE;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(nameUpper, centerX, nameY);

    // Divider
    const dividerY = nameY + nameSize / 2 + 40;
    ctx.fillStyle = LAVENDER;
    ctx.fillRect(centerX - 45, dividerY, 90, 5);

    // Subtitle"Verified Artist on PayStreem" with optional check badge
    const subY = dividerY + 70;
    ctx.font = `500 42px"Inter","Helvetica Neue", Arial, sans-serif`;
    const part1 = isVerified ? `Verified ${roleLabel} on` : `${roleLabel} on`;
    const part2 = "PayStreem";
    const w1 = ctx.measureText(part1).width;
    ctx.font = `700 42px"Inter","Helvetica Neue", Arial, sans-serif`;
    const w2 = ctx.measureText(part2).width;
    const badgeSize = isVerified ? 44 : 0;
    const badgeGap = isVerified ? 14 : 0;
    const totalW = badgeSize + badgeGap + w1 + w2;
    let cursorX = centerX - totalW / 2;

    if (isVerified) {
      // Purple circle badge with white check
      const bcx = cursorX + badgeSize / 2;
      const bcy = subY;
      ctx.fillStyle = DEEP_PURPLE;
      ctx.beginPath();
      ctx.arc(bcx, bcy, badgeSize / 2, 0, 2 * Math.PI);
      ctx.fill();
      // Check mark
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(bcx - 10, bcy + 1);
      ctx.lineTo(bcx - 2, bcy + 9);
      ctx.lineTo(bcx + 11, bcy - 7);
      ctx.stroke();
      cursorX += badgeSize + badgeGap;
    }

    ctx.font = `500 42px"Inter","Helvetica Neue", Arial, sans-serif`;
    ctx.fillStyle = GRAY;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(part1, cursorX, subY);
    ctx.fillStyle = DEEP_PURPLE;
    ctx.font = `700 42px"Inter","Helvetica Neue", Arial, sans-serif`;
    ctx.fillText(part2, cursorX + w1, subY);

    // QR code with corner brackets
    const qrSize = 380;
    const qrPad = 24;
    const qrX = centerX - qrSize / 2;
    const qrY = subY + 90;

    // White rounded background
    const qrBgX = qrX - qrPad;
    const qrBgY = qrY - qrPad;
    const qrBgSize = qrSize + qrPad * 2;
    const radius = 18;
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.moveTo(qrBgX + radius, qrBgY);
    ctx.lineTo(qrBgX + qrBgSize - radius, qrBgY);
    ctx.quadraticCurveTo(
      qrBgX + qrBgSize,
      qrBgY,
      qrBgX + qrBgSize,
      qrBgY + radius,
    );
    ctx.lineTo(qrBgX + qrBgSize, qrBgY + qrBgSize - radius);
    ctx.quadraticCurveTo(
      qrBgX + qrBgSize,
      qrBgY + qrBgSize,
      qrBgX + qrBgSize - radius,
      qrBgY + qrBgSize,
    );
    ctx.lineTo(qrBgX + radius, qrBgY + qrBgSize);
    ctx.quadraticCurveTo(
      qrBgX,
      qrBgY + qrBgSize,
      qrBgX,
      qrBgY + qrBgSize - radius,
    );
    ctx.lineTo(qrBgX, qrBgY + radius);
    ctx.quadraticCurveTo(qrBgX, qrBgY, qrBgX + radius, qrBgY);
    ctx.closePath();
    ctx.fill();

    ctx.drawImage(qrCodeImg, qrX, qrY, qrSize, qrSize);

    // Corner brackets
    ctx.strokeStyle = LAVENDER;
    ctx.lineWidth = 8;
    ctx.lineCap = "round";
    const bL = 40;
    const bOff = 6;
    const corners = [
      { x: qrBgX - bOff, y: qrBgY - bOff, dx: 1, dy: 1 },
      { x: qrBgX + qrBgSize + bOff, y: qrBgY - bOff, dx: -1, dy: 1 },
      { x: qrBgX - bOff, y: qrBgY + qrBgSize + bOff, dx: 1, dy: -1 },
      {
        x: qrBgX + qrBgSize + bOff,
        y: qrBgY + qrBgSize + bOff,
        dx: -1,
        dy: -1,
      },
    ];
    corners.forEach(({ x, y, dx, dy }) => {
      ctx.beginPath();
      ctx.moveTo(x, y + bL * dy);
      ctx.lineTo(x, y);
      ctx.lineTo(x + bL * dx, y);
      ctx.stroke();
    });

    // Output PNG
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to create watermarked image blob"));
      }, "image/png");
    });
  } catch (error) {
    console.error("Error creating watermarked avatar:", error);
    throw error;
  }
}
